import { CircularProgress, Typography } from '@material-ui/core';
import * as d3 from 'd3';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';
import ResponsiveContainer from '../ResponsiveContainer';
import { rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toPercent } from '../../modules/text';
import CountyContext from '../../contexts/CountyContext';
import CustomTooltip from '../CustomTooltip';
import NoDataVisual from '../NoDataVisual';
import StoryContext from '../../contexts/StoryContext';

function PercentBySettingType({ settingTypeName, settingTypeCode, height }) {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const { primaryColor, secondaryColor, darkColor } = useContext(StoryContext);
  const { countyCode, county } = useContext(CountyContext);

  useEffect(() => {
    setError(null);
    async function getData() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND license_type=LIC
      AND (setting_type=HOM OR setting_type=CEN)`;

      if (countyCode) {
        whereClause += ` AND county_fips=${countyCode}`;
      }

      rdsTabulate('ccdc', 'provider', {
        dims: 'state_fips,setting_type',
        measure: 'count:COUNT(provider_id)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
      })
        .then((result) => {
          const d = result.parsedBody.dataProvider;
          const total = d.reduce((a, v) => (a + Number.parseInt(v.count, 10)), 0);
          const payload = d.map((v) => Object.assign(v, { value: ((v.count / total) * 100) }));
          setData(payload);
        })
        .catch((rdsError) => {
          setError(rdsError);
        })
        .finally(() => {
          setReady(true);
        });
    }
    if (stateCode && states[stateCode.toUpperCase()]) {
      // fetch actual average price data
      getData();
    }
  }, [stateCode, countyCode]);

  const colorsGradient = d3.scaleLinear().range([darkColor, secondaryColor]).domain([0, data.length - 1]);

  const formatterFunction = function formatterFunction(payload) {
    return `Of the providers who offer NSH care, <b>${toPercent(payload.value / 100)}</b> are ${payload.name.toLowerCase()}.`;
  };

  if (!ready) {
    return <CircularProgress />;
  }

  let title = 'Setting Type';
  if (county) {
    title += ` - ${county} County`;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <Typography variant="body2" align="center"><strong>{title}</strong></Typography>
      {data.length === 0 && <NoDataVisual message="Not reported." />}
      {(data.length > 0)
        && (
          <ResponsiveContainer height={height}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="setting_type"
                startAngle={0}
                outerRadius={height / 3.2}
                label={({
                  cx,
                  cy,
                  midAngle,
                  innerRadius,
                  outerRadius,
                  value,
                  percent,
                }) => {
                  const RADIAN = Math.PI / 180;
                  // eslint-disable-next-line
                  const radius = 25 + innerRadius + (outerRadius - innerRadius);
                  // eslint-disable-next-line
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  // eslint-disable-next-line
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);

                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#000"
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                    >
                      {toPercent(percent)}
                    </text>
                  );
                }}
                labelLine
                fill={primaryColor}
              >
                {data.map((entry, index) => (
                  <Cell key={entry} fill={colorsGradient(index)} />
                ))}
              </Pie>
              <Tooltip cursor={false} content={<CustomTooltip primaryColor={primaryColor} formatterFunction={formatterFunction} />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
    </>
  );
}

export default React.memo(PercentBySettingType);
